import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiArrowLeft, FiBook, FiCheckCircle, FiPlay, FiAward, FiGrid, FiList } from 'react-icons/fi';
import CertificateModal from './CertificateModal';

import { API_URL } from '../config';

function Training() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [progress, setProgress] = useState({});
  const [completions, setCompletions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [selectedCompletion, setSelectedCompletion] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    loadTrainings();
    loadProgress();
    loadCompletions();
  }, []);

  const loadTrainings = async () => {
    try {
      setError(null);
      const response = await axios.get(`${API_URL}/trainings`);
      console.log('Trainings response:', response.data);
      if (response.data && Array.isArray(response.data.trainings)) {
        setTrainings(response.data.trainings);
      } else if (response.data && response.data.trainings === undefined) {
        // Handle case where response doesn't have trainings property
        console.warn('Response missing trainings property, using empty array');
        setTrainings([]);
      } else {
        setTrainings(response.data.trainings || []);
      }
    } catch (error) {
      console.error('Error loading trainings:', error);
      console.error('Error response:', error.response?.data);
      setError(error.response?.data?.error || error.message || 'Failed to load trainings');
      setTrainings([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const response = await axios.get(`${API_URL}/training/progress/my`);
      setProgress(response.data.progress);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const loadCompletions = async () => {
    try {
      const response = await axios.get(`${API_URL}/trainings/completions/my`);
      setCompletions(response.data.completions || {});
    } catch (error) {
      console.error('Error loading completions:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb] mb-4"></div>
          <div className="text-gray-600">Loading trainings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Header */}
      <header className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-5 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Trainings</h1>
            <p className="text-sm sm:text-base text-gray-700 mt-1 font-medium">Access and complete your training courses</p>
          </div>
          <div className="flex items-center gap-1 bg-white/70 border border-white/50 rounded-xl p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-[#3c79bb] text-white' : 'text-gray-700 hover:bg-white/80'}`}
              aria-label="Grid view"
              title="Grid view"
            >
              <FiGrid className="text-base" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#3c79bb] text-white' : 'text-gray-700 hover:bg-white/80'}`}
              aria-label="List view"
              title="List view"
            >
              <FiList className="text-base" />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {error ? (
          <div className="glass-card rounded-2xl p-6 sm:p-8 lg:p-10 text-center border-2 border-red-300">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-red-900 mb-2">Error Loading Trainings</h2>
            <p className="text-red-800 text-sm sm:text-base font-medium mb-4">{error}</p>
            <button
              onClick={loadTrainings}
              className="px-4 py-2 bg-[#3c79bb] text-white rounded-lg hover:bg-[#2d5a8a] transition-colors font-medium"
            >
              Retry
            </button>
          </div>
        ) : trainings.length === 0 ? (
          <div className="glass-card rounded-2xl p-6 sm:p-8 lg:p-10 text-center">
            <FiBook className="text-5xl sm:text-6xl text-gray-400 mx-auto mb-4" />
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">No Trainings Available</h2>
            <p className="text-gray-800 text-sm sm:text-base font-medium">Check back later for new training content.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
            {trainings.map((training) => {
              const isCompleted = !!completions[training.id];
              const completion = completions[training.id];
              return (
                <div
                  key={training.id}
                  className={`glass-card rounded-2xl p-5 sm:p-6 hover:glass-strong hover:scale-[1.02] transition-all duration-300 cursor-pointer border-l-4 ${isCompleted ? 'border-green-500' : 'border-[#3c79bb]'} group`}
                  onClick={() => navigate(`/training/${training.id}`)}
                >
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-[#3c79bb] transition-colors truncate">{training.title}</h3>
                      {training.description && (
                        <p className="text-gray-800 text-xs sm:text-sm mb-2 line-clamp-2 leading-relaxed font-medium">{training.description}</p>
                      )}
                    </div>
                    {isCompleted && (
                      <FiAward className="text-green-500 text-xl sm:text-2xl flex-shrink-0 ml-2" />
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-white/20">
                    <span className="text-xs text-gray-700 truncate mr-2 font-medium">
                      Created by {training.created_by_name}
                    </span>
                    {isCompleted ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCompletion(completion);
                          setShowCertificate(true);
                        }}
                        className="text-xs font-bold text-green-800 glass-light bg-green-100/50 px-2 py-1 rounded-full whitespace-nowrap hover:bg-green-200/50 transition-colors flex items-center gap-1"
                      >
                        <FiAward className="text-sm" />
                        Certificate
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {trainings.map((training) => {
              const isCompleted = !!completions[training.id];
              const completion = completions[training.id];
              return (
                <div
                  key={training.id}
                  className={`glass-card rounded-2xl p-4 sm:p-5 border-l-4 cursor-pointer transition-all duration-200 hover:glass-strong ${
                    isCompleted ? 'border-green-500' : 'border-[#3c79bb]'
                  }`}
                  onClick={() => navigate(`/training/${training.id}`)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{training.title}</h3>
                      {training.description && (
                        <p className="text-gray-800 text-sm mt-1 line-clamp-2 font-medium">{training.description}</p>
                      )}
                      <p className="text-xs text-gray-700 mt-2 font-medium">
                        Created by {training.created_by_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isCompleted && <FiAward className="text-green-500 text-xl" />}
                      {isCompleted ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCompletion(completion);
                            setShowCertificate(true);
                          }}
                          className="text-xs font-bold text-green-800 glass-light bg-green-100/50 px-2.5 py-1.5 rounded-full whitespace-nowrap hover:bg-green-200/50 transition-colors flex items-center gap-1"
                        >
                          <FiAward className="text-sm" />
                          Certificate
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Certificate Modal */}
      {showCertificate && selectedCompletion && (
        <CertificateModal
          completion={selectedCompletion}
          onClose={() => {
            setShowCertificate(false);
            setSelectedCompletion(null);
          }}
        />
      )}
    </div>
  );
}

export default Training;
